// Local time helpers for Europe/Amsterdam. Dates are ISO strings (YYYY-MM-DD), clock times "HH:MM" or "HH:MM:SS".
// A planning day runs from day_starts_at (04:00) to 04:00 the next calendar day (spec L18).

export const TIME_ZONE = "Europe/Amsterdam";
export const DEFAULT_DAY_START_MINUTES = 4 * 60;

const partsFormat = new Intl.DateTimeFormat("en-CA", {
  timeZone: TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

export interface LocalParts {
  date: string;
  minutes: number;
}

/** Calendar date and minutes since midnight of an instant, in Europe/Amsterdam. */
export function localParts(instant: Date): LocalParts {
  const parts = Object.fromEntries(partsFormat.formatToParts(instant).map((p) => [p.type, p.value]));
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    minutes: Number(parts.hour) * 60 + Number(parts.minute),
  };
}

/** The planning day an instant belongs to: before the day start it still counts as the previous date. */
export function planningDate(instant: Date, dayStartMinutes = DEFAULT_DAY_START_MINUTES): string {
  const { date, minutes } = localParts(instant);
  return minutes < dayStartMinutes ? addDays(date, -1) : date;
}

/** Minutes since midnight of the planning day; after midnight this continues past 1440. */
export function planningMinutes(instant: Date, dayStartMinutes = DEFAULT_DAY_START_MINUTES): number {
  const { minutes } = localParts(instant);
  return minutes < dayStartMinutes ? minutes + 1440 : minutes;
}

/**
 * After bedtime (B49, L130): from the start of the bedtime step until the day switches. A bedtime after midnight
 * (00:30) lies after the day's start in planning minutes, like the clock of the evening before.
 */
export function afterBedtime(minute: number, bedtime: string, dayStartMinutes = DEFAULT_DAY_START_MINUTES): boolean {
  const start = timeToMinutes(bedtime.slice(0, 5));
  return minute >= (start < dayStartMinutes ? start + 1440 : start);
}

export function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** ISO weekday: 1 = Monday ... 7 = Sunday. */
export function isoWeekday(isoDate: string): number {
  const day = new Date(`${isoDate}T00:00:00Z`).getUTCDay();
  return day === 0 ? 7 : day;
}

/** "07:30" or "07:30:00" to minutes since midnight. */
export function timeToMinutes(time: string): number {
  const [h, m] = time.split(":");
  return Number(h) * 60 + Number(m);
}

/** Minutes to "07:30". */
export function minutesToTime(minutes: number): string {
  const m = ((minutes % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

/** "07:30:00" to "7:30", the way times are written in the UI. */
export function displayTime(time: string): string {
  const [h, m] = time.split(":");
  return `${Number(h)}:${m}`;
}

const longDate = new Intl.DateTimeFormat("nl-NL", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" });

/** "maandag 12 oktober". */
export function displayDate(isoDate: string): string {
  return longDate.format(new Date(`${isoDate}T00:00:00Z`));
}

/**
 * The instant of a local clock time in Europe/Amsterdam ("2026-10-25T02:30"). A time that does not exist
 * (the hour skipped in March) moves forward by the gap; a time that exists twice (October) takes the first.
 */
export function localToInstant(localDateTime: string): Date {
  const [d, t = "00:00"] = localDateTime.split("T");
  const [y, m, day] = d!.split("-").map(Number);
  const [h, min, s = 0] = t.split(":").map(Number);
  const naive = Date.UTC(y!, m! - 1, day!, h!, min!, s);
  // Amsterdam is UTC+1 (winter) or UTC+2 (summer): try both.
  const first = naive - 60 * 60000;
  const second = naive - 120 * 60000;
  const wanted = { date: d!, minutes: h! * 60 + min! };
  const matches = [first, second].filter((c) => {
    const p = localParts(new Date(c));
    return p.date === wanted.date && p.minutes === wanted.minutes;
  });
  // October overlap: both match, take the first (summer time). March gap: none match, move forward.
  return new Date(matches.length ? Math.min(...matches) : Math.max(first, second));
}

/** "2026-10-14T17:00:00" from a date and a clock time. */
export function joinLocal(date: string, time: string): string {
  return `${date}T${time.length === 5 ? `${time}:00` : time}`;
}
